import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, Send, Plus, Ticket, Paperclip, Search, Upload, Star } from 'lucide-react';
import { supporto } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import DataCard from '../components/shared/DataCard';

// ── Helper ────────────────────────────────────────────────────────────────────

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;
const ROW_BG = (i) => (i % 2 === 0 ? 'var(--ds-bg)' : 'var(--chip)');
const ROW_HOVER = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))';

// ── Costanti ──────────────────────────────────────────────────────────────────

const STATI_BADGE = {
  APERTO:          { label: 'Aperto',          bg: tint('var(--zone-blue)', 14), color: 'var(--zone-blue)' },
  IN_LAVORAZIONE:  { label: 'In lavorazione',  bg: tint('var(--warn)', 14),      color: 'var(--warn)' },
  IN_ATTESA:       { label: 'In attesa',       bg: tint('var(--warn)', 14),      color: 'var(--warn)' },
  RISOLTO:         { label: 'Risolto',         bg: tint('var(--ok)', 14),        color: 'var(--ok)' },
};

const TIPOLOGIE_BADGE = {
  AI_VOCE:      { label: 'AI / Voce',       bg: tint('var(--zone-violet)', 14),  color: 'var(--zone-violet)' },
  CANTIERI:     { label: 'Cantieri',         bg: tint('var(--zone-blue)', 14),    color: 'var(--zone-blue)' },
  FATTURAZIONE: { label: 'Fatturazione',     bg: tint('var(--zone-amber)', 14),   color: 'var(--zone-amber)' },
  WHATSAPP:     { label: 'WhatsApp',         bg: tint('var(--ok)', 14),           color: 'var(--ok)' },
  ACCOUNT:      { label: 'Account',          bg: tint('var(--ds-danger)', 14),    color: 'var(--ds-danger)' },
  PDF_DOCS:     { label: 'PDF / Documenti',  bg: tint('var(--zone-magenta)', 14), color: 'var(--zone-magenta)' },
  ALTRO:        { label: 'Altro',            bg: 'var(--chip)',                   color: 'var(--ds-muted)' },
};

const TIPOLOGIE = [
  { value: 'AI_VOCE',      label: 'AI / Voce' },
  { value: 'CANTIERI',     label: 'Cantieri' },
  { value: 'FATTURAZIONE', label: 'Fatturazione' },
  { value: 'WHATSAPP',     label: 'WhatsApp' },
  { value: 'ACCOUNT',      label: 'Account' },
  { value: 'PDF_DOCS',     label: 'PDF / Documenti' },
  { value: 'ALTRO',        label: 'Altro' },
];

const PRIORITA = [
  { value: 'BASSA', label: 'Bassa' },
  { value: 'MEDIA', label: 'Media' },
  { value: 'ALTA',  label: 'Alta' },
];

const fmtDate = (d) => new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtDateTime = (d) => new Date(d).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// ── Componente Principale ─────────────────────────────────────────────────────

export default function Supporto() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const location = useLocation();
  const [vista, setVista] = useState('lista');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Filtri (1C)
  const [filtroTesto, setFiltroTesto] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroPriorita, setFiltroPriorita] = useState('');
  const [filtroDa, setFiltroDa] = useState('');
  const [filtroA, setFiltroA] = useState('');

  const caricaTickets = useCallback(() => {
    setLoading(true);
    setError(null);
    supporto.list()
      .then(res => setTickets(res.data?.results || res.data || []))
      .catch(() => setError('Errore nel caricamento dei ticket.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { caricaTickets(); }, [caricaTickets]);

  // Marca come visti
  useEffect(() => {
    if (tickets.length > 0) {
      const apertiIds = tickets.filter(t => t.stato !== 'RISOLTO').map(t => t.id);
      const visti = JSON.parse(localStorage.getItem('supporto_seen_ids') || '[]');
      const merged = [...new Set([...visti, ...apertiIds])];
      localStorage.setItem('supporto_seen_ids', JSON.stringify(merged));
    }
  }, [tickets]);

  const apriDettaglio = (id) => { setSelectedId(id); setVista('dettaglio'); };
  const tornaALista = () => { setVista('lista'); setSelectedId(null); caricaTickets(); };

  // Deep-link da centro notifiche: /supporto?ticket={id} apre il dettaglio
  useEffect(() => {
    const ticketId = new URLSearchParams(location.search).get('ticket');
    if (ticketId) { setSelectedId(ticketId); setVista('dettaglio'); }
  }, [location.search]);

  if (vista === 'nuovo') return <NuovoTicketForm onCreated={(t) => { setSelectedId(t.id); setVista('dettaglio'); caricaTickets(); }} onCancel={tornaALista} />;
  if (vista === 'dettaglio' && selectedId) return <DettaglioTicket ticketId={selectedId} onBack={tornaALista} userId={user?.id} onRefreshList={caricaTickets} />;

  // ── KPI (1A) ────────────────────────────────────────────────────────────────
  const totale = tickets.length;
  const inAttesa = tickets.filter(t => t.stato === 'IN_ATTESA').length;
  const trentaGgFa = new Date(Date.now() - 30 * 86400000);
  const risoltiMese = tickets.filter(t => t.stato === 'RISOLTO' && t.risolto_il && new Date(t.risolto_il) >= trentaGgFa).length;

  // ── Filtri applicati lato frontend (1C) ─────────────────────────────────────
  const filtrati = tickets.filter(t => {
    if (filtroStato && t.stato !== filtroStato) return false;
    if (filtroPriorita && t.priorita !== filtroPriorita) return false;
    if (filtroTesto) {
      const q = filtroTesto.toLowerCase();
      const matchNum = String(t.numero).padStart(5, '0').includes(q) || String(t.numero).includes(q);
      const matchOgg = t.oggetto.toLowerCase().includes(q);
      if (!matchNum && !matchOgg) return false;
    }
    if (filtroDa && new Date(t.aperto_il) < new Date(filtroDa)) return false;
    if (filtroA) {
      const fine = new Date(filtroA);
      fine.setHours(23, 59, 59);
      if (new Date(t.aperto_il) > fine) return false;
    }
    return true;
  });

  // ── Lista ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '28px 32px' }}>
      {/* Header + tenant info (1E) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', margin: 0 }}>Supporto</h1>
          <p style={{ fontSize: 14, color: 'var(--ds-muted)', margin: '4px 0 0' }}>Le tue richieste di assistenza</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)' }}>{user?.tenant?.nome || ''}</div>
            {user?.tenant?.id && <div style={{ fontSize: 10, color: 'var(--ds-muted)' }}>ID: {String(user.tenant.id).slice(0, 8)}</div>}
          </div>
          <button onClick={() => setVista('nuovo')} style={btnPrimary}>
            <Plus size={14} strokeWidth={1.8} color="#fff" /> Nuovo ticket
          </button>
        </div>
      </div>

      {/* KPI cards (1A) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <KpiCard label="Ticket totali" value={totale} borderColor="var(--brand)" />
        <KpiCard label="In attesa risposta" value={inAttesa} borderColor="var(--warn)" />
        <KpiCard label="Risolti (30gg)" value={risoltiMese} borderColor="var(--ok)" />
      </div>

      {/* Filtri (1C) */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ position: 'relative', flex: '1 1 180px' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <Search size={14} strokeWidth={1.8} color="var(--ds-muted)" />
          </span>
          <input placeholder="Cerca per numero o oggetto..." value={filtroTesto} onChange={e => setFiltroTesto(e.target.value)} style={{ ...inputStyle, paddingLeft: 32, fontSize: 12 }} />
        </div>
        <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)} style={{ ...selectStyle }}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATI_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filtroPriorita} onChange={e => setFiltroPriorita(e.target.value)} style={{ ...selectStyle }}>
          <option value="">Tutte le priorita</option>
          {PRIORITA.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <input type="date" value={filtroDa} onChange={e => setFiltroDa(e.target.value)} style={{ ...selectStyle, width: 130 }} title="Da" />
        <input type="date" value={filtroA} onChange={e => setFiltroA(e.target.value)} style={{ ...selectStyle, width: 130 }} title="A" />
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-muted)' }}>Caricamento...</div>}
      {error && <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-danger)' }}>{error}</div>}

      {!loading && !error && tickets.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <Ticket size={48} strokeWidth={1.8} color="var(--ds-border)" />
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', marginTop: 16 }}>Nessuna richiesta di supporto</div>
          <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginTop: 4 }}>Hai bisogno di aiuto? Apri un ticket.</div>
        </div>
      )}

      {!loading && !error && filtrati.length > 0 && (isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtrati.map((t) => {
            const statoBadge = STATI_BADGE[t.stato] || STATI_BADGE.APERTO;
            const tipoBadge = TIPOLOGIE_BADGE[t.tipologia] || TIPOLOGIE_BADGE.ALTRO;
            return (
              <DataCard
                key={t.id}
                title={t.oggetto}
                subtitle={`#${String(t.numero).padStart(5, '0')} · ${t.ultimo_aggiornamento ? fmtDateTime(t.ultimo_aggiornamento) : fmtDate(t.aperto_il)}`}
                onClick={() => apriDettaglio(t.id)}
                badge={<span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: statoBadge.bg, color: statoBadge.color, fontWeight: 500, whiteSpace: 'nowrap' }}>{statoBadge.label}</span>}
                meta={[
                  { label: 'Tipologia', value: <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: tipoBadge.bg, color: tipoBadge.color, fontWeight: 500 }}>{tipoBadge.label}</span> },
                  { label: 'Priorità', value: t.priorita },
                  t.has_allegati ? { label: 'Allegati', value: <Paperclip size={13} strokeWidth={1.8} color="var(--ds-muted)" /> } : null,
                ]}
              />
            );
          })}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', boxShadow: CARD_SHADOW }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--chip)' }}>
                {['Numero', 'Oggetto', 'Tipologia', 'Stato', 'Priorita', 'Aggiornato', ''].map(h => (
                  <th key={h || 'action'} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid var(--ds-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrati.map((t, i) => {
                const statoBadge = STATI_BADGE[t.stato] || STATI_BADGE.APERTO;
                const tipoBadge = TIPOLOGIE_BADGE[t.tipologia] || TIPOLOGIE_BADGE.ALTRO;
                return (
                  <tr key={t.id} onClick={() => apriDettaglio(t.id)}
                    style={{ cursor: 'pointer', background: ROW_BG(i) }}
                    onMouseEnter={e => e.currentTarget.style.background = ROW_HOVER}
                    onMouseLeave={e => e.currentTarget.style.background = ROW_BG(i)}
                  >
                    <td style={{ padding: '12px 16px', fontWeight: 500, color: 'var(--ink)' }}>
                      #{String(t.numero).padStart(5, '0')}
                      {t.has_allegati && <span style={{ marginLeft: 6, display: 'inline-flex', verticalAlign: 'middle' }}><Paperclip size={12} strokeWidth={1.8} color="var(--ds-muted)" /></span>}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--ds-text)' }}>{t.oggetto}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: tipoBadge.bg, color: tipoBadge.color, fontWeight: 500 }}>{tipoBadge.label}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: statoBadge.bg, color: statoBadge.color, fontWeight: 500 }}>{statoBadge.label}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{t.priorita}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--ds-muted)', fontSize: 11 }}>{t.ultimo_aggiornamento ? fmtDateTime(t.ultimo_aggiornamento) : fmtDate(t.aperto_il)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={e => { e.stopPropagation(); apriDettaglio(t.id); }} style={{ ...linkBtn, color: 'var(--brand)', fontSize: 12, fontWeight: 500 }}>Dettagli →</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {!loading && !error && tickets.length > 0 && filtrati.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>Nessun ticket corrisponde ai filtri selezionati.</div>
      )}
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, borderColor }) {
  return (
    <div style={{
      background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
      borderTop: '3px solid ' + borderColor, borderRadius: 'var(--radius-card)', padding: '20px 24px',
      boxShadow: CARD_SHADOW,
    }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', marginTop: 4 }}>{value}</div>
    </div>
  );
}

// ── Form Nuovo Ticket ─────────────────────────────────────────────────────────

function NuovoTicketForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ oggetto: '', descrizione: '', tipologia: 'ALTRO', priorita: 'MEDIA' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.oggetto.trim() || !form.descrizione.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await supporto.create(form);
      onCreated(res.data);
    } catch {
      setError('Errore nell\'invio della richiesta. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '28px 32px', maxWidth: 640, margin: '0 auto' }}>
      <button onClick={onCancel} style={{ ...linkBtn, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ArrowLeft size={14} strokeWidth={1.8} color="var(--ds-muted)" /> Torna alla lista
      </button>

      <h1 style={{ fontSize: 26, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', margin: '0 0 24px' }}>Nuova richiesta di supporto</h1>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle}>Oggetto *</label>
          <input value={form.oggetto} onChange={e => setForm({ ...form, oggetto: e.target.value })} placeholder="Breve descrizione del problema" style={inputStyle} required />
        </div>
        <div>
          <label style={labelStyle}>Descrizione *</label>
          <textarea value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} placeholder="Descrivi il problema in dettaglio..." rows={5} style={{ ...inputStyle, resize: 'vertical' }} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Tipologia</label>
            <select value={form.tipologia} onChange={e => setForm({ ...form, tipologia: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
              {TIPOLOGIE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Priorita</label>
            <select value={form.priorita} onChange={e => setForm({ ...form, priorita: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
              {PRIORITA.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        {error && <div style={{ fontSize: 13, color: 'var(--ds-danger)', padding: '8px 12px', borderRadius: 'var(--radius-base)', background: tint('var(--ds-danger)', 12) }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" onClick={onCancel} style={btnCancel}>Annulla</button>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Invio...' : 'Invia richiesta'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Dettaglio Ticket ──────────────────────────────────────────────────────────

function DettaglioTicket({ ticketId, onBack, userId, onRefreshList }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testo, setTesto] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);
  const fileInputRef = useRef(null);

  // Drag & drop
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Valutazione (1G)
  const [showRating, setShowRating] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);

  const carica = useCallback(() => {
    setLoading(true);
    supporto.detail(ticketId)
      .then(res => {
        setTicket(res.data);
        // Mostra rating se risolto e non ancora valutato
        if (res.data.stato === 'RISOLTO' && res.data.valutazione === null) {
          setShowRating(true);
        }
        // Marca come visto
        const visti = JSON.parse(localStorage.getItem('supporto_seen_ids') || '[]');
        if (!visti.includes(ticketId)) {
          localStorage.setItem('supporto_seen_ids', JSON.stringify([...visti, ticketId]));
        }
      })
      .catch(() => setError('Errore nel caricamento del ticket.'))
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [ticket?.messaggi]);

  const inviaMessaggio = async () => {
    if (!testo.trim() || sending) return;
    const msgTemp = { id: 'temp-' + Date.now(), testo, autore: userId, autore_nome: 'Tu', creato_il: new Date().toISOString(), is_internal: false };
    setTicket(prev => ({ ...prev, messaggi: [...(prev.messaggi || []), msgTemp] }));
    const testoInvio = testo;
    setTesto('');
    setSending(true);
    try {
      await supporto.inviaMessaggio(ticketId, testoInvio);
      carica();
    } catch {
      setTicket(prev => ({ ...prev, messaggi: (prev.messaggi || []).filter(m => m.id !== msgTemp.id) }));
      setTesto(testoInvio);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); inviaMessaggio(); }
  };

  // ── Valutazione handler (1G) ────────────────────────────────────────────────
  const inviaValutazione = async (val) => {
    try {
      await supporto.valutazione(ticketId, val);
      setTicket(prev => ({ ...prev, valutazione: val }));
    } catch { /* silenzioso */ }
    setShowRating(false);
  };

  // ── File upload handler ─────────────────────────────────────────────────────
  const handleFileSelect = async (file) => {
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) { alert('Formato non supportato. Usa PDF, JPG, PNG, DOC o DOCX.'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('File troppo grande. Massimo 10 MB.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await supporto.uploadAllegato(ticketId, fd);
      carica();
      if (onRefreshList) onRefreshList();
    } catch {
      alert('Errore durante l\'upload del file.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-muted)' }}>Caricamento...</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-danger)' }}>{error}</div>;
  if (!ticket) return null;

  const badge = STATI_BADGE[ticket.stato] || STATI_BADGE.APERTO;
  const messaggi = (ticket.messaggi || []).filter(m => !m.is_internal);
  const allegati = ticket.allegati || [];
  const isRisolto = ticket.stato === 'RISOLTO';

  return (
    <div style={{ padding: '28px 32px 12px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)' }}>
      {/* Breadcrumb */}
      <div style={{ flexShrink: 0 }}>
        <button onClick={onBack} style={{ ...linkBtn, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeft size={14} strokeWidth={1.8} color="var(--ds-muted)" /> Supporto &gt; #{String(ticket.numero).padStart(5, '0')}
        </button>

        {/* Header */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <h1 style={{ fontSize: 20, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', margin: 0 }}>#{String(ticket.numero).padStart(5, '0')}</h1>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: badge.bg, color: badge.color, fontWeight: 500 }}>{badge.label}</span>
          </div>
          <div style={{ fontSize: 15, color: 'var(--ink)', fontWeight: 500 }}>{ticket.oggetto}</div>
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>Aperto il {fmtDateTime(ticket.aperto_il)}</div>
        </div>
      </div>

      {/* Thread messaggi — sfondo bianco, stile chat */}
      <div
        ref={threadRef}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)',
          padding: '20px 24px',
          display: 'flex', flexDirection: 'column', gap: 10,
          ...(dragging ? { background: tint('var(--brand)', 6), border: '2px dashed var(--brand)' } : {}),
        }}
      >
        {dragging && (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--brand)', fontSize: 13, fontWeight: 500 }}>
            <Upload size={24} strokeWidth={1.8} color="var(--brand)" />
            <div style={{ marginTop: 8 }}>Rilascia il file qui</div>
          </div>
        )}
        {!dragging && messaggi.map(m => {
          const isMio = m.autore === userId;
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: isMio ? 'flex-end' : 'flex-start', gap: 8 }}>
              {!isMio && (
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' }}>
                  <span style={{ color: '#fff', fontSize: 11, fontWeight: 500 }}>M</span>
                </div>
              )}
              <div style={{
                maxWidth: '70%', padding: '10px 14px', borderRadius: 12,
                background: isMio ? 'var(--brand)' : 'var(--chip)',
                color: isMio ? '#fff' : 'var(--ds-text)',
                fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: isMio ? 'rgba(255,255,255,0.85)' : 'var(--ds-muted)' }}>
                    {isMio ? 'Tu' : (m.autore_nome || 'mAIstro Supporto')}
                  </span>
                  <span style={{ fontSize: 10, color: isMio ? 'rgba(255,255,255,0.6)' : 'var(--ds-muted)' }}>
                    {fmtDateTime(m.creato_il)}
                  </span>
                </div>
                {m.testo}
              </div>
            </div>
          );
        })}

        {/* Allegati inline nel thread */}
        {!dragging && allegati.map(a => {
          const isMio = !a.is_admin;
          return (
            <div key={a.id} style={{ display: 'flex', justifyContent: isMio ? 'flex-end' : 'flex-start', gap: 8 }}>
              {!isMio && (
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' }}>
                  <span style={{ color: '#fff', fontSize: 11, fontWeight: 500 }}>M</span>
                </div>
              )}
              <div style={{
                maxWidth: '70%', padding: '8px 12px', borderRadius: 12,
                background: isMio ? 'var(--brand)' : 'var(--chip)',
                color: isMio ? '#fff' : 'var(--ds-text)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Paperclip size={12} strokeWidth={1.8} color={isMio ? '#fff' : 'var(--ds-muted)'} />
                <span style={{ fontSize: 12 }}>{a.nome_file}</span>
                <span style={{ fontSize: 10, opacity: 0.7 }}>({Math.round(a.dimensione / 1024)} KB)</span>
              </div>
            </div>
          );
        })}

        {/* Valutazione post-risoluzione */}
        {!dragging && isRisolto && showRating && (
          <div style={{ padding: '16px 20px', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', background: 'var(--chip)', textAlign: 'center', margin: '8px 0' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 12 }}>Come valuteresti il supporto ricevuto?</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 10 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n}
                  onMouseEnter={() => setHoverStar(n)}
                  onMouseLeave={() => setHoverStar(0)}
                  onClick={() => inviaValutazione(n)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, transition: 'color 0.15s' }}
                >
                  <Star size={22} strokeWidth={1.8} fill={n <= hoverStar ? 'var(--warn)' : 'none'} color={n <= hoverStar ? 'var(--warn)' : 'var(--ds-border)'} />
                </button>
              ))}
            </div>
            <button onClick={() => setShowRating(false)} style={{ ...linkBtn, fontSize: 12, color: 'var(--ds-muted)' }}>Salta</button>
          </div>
        )}

        {!dragging && isRisolto && !showRating && (
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
            Questo ticket e stato risolto.{ticket.valutazione ? ` Valutazione: ${'★'.repeat(ticket.valutazione)}${'☆'.repeat(5 - ticket.valutazione)}` : ''} Rispondi per riaprirlo.
          </div>
        )}
      </div>

      {/* Composer — ancorato in basso, max-width per non sovrapporre ChatWidget */}
      <div style={{ flexShrink: 0, padding: '8px 0 0', borderTop: '1px solid var(--ds-border)', marginBottom: 8, maxWidth: 'calc(100% - 80px)' }}>
        {uploading && <div style={{ fontSize: 12, color: 'var(--brand)', marginBottom: 4 }}>Caricamento file in corso...</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => { if (e.target.files[0]) handleFileSelect(e.target.files[0]); e.target.value = ''; }} />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 4px', flexShrink: 0, opacity: uploading ? 0.5 : 1 }} title="Allega file">
            <Paperclip size={18} strokeWidth={1.8} color="var(--ds-muted)" />
          </button>
          <textarea
            value={testo}
            onChange={e => setTesto(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scrivi un messaggio..."
            rows={2}
            style={{ ...inputStyle, flex: 1, resize: 'none' }}
          />
          <button onClick={inviaMessaggio} disabled={!testo.trim() || sending} style={{ ...btnPrimary, padding: '10px 16px', opacity: (!testo.trim() || sending) ? 0.5 : 1 }}>
            <Send size={14} strokeWidth={1.8} color="#fff" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Stili ──────────────────────────────────────────────────────────────────────

const btnPrimary = { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnCancel = { padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 13 };
const linkBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 13, padding: 0 };
const inputStyle = { padding: '10px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 13, width: '100%', background: 'var(--ds-bg)', color: 'var(--ds-text)' };
const selectStyle = { padding: '8px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 12, background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer' };
const labelStyle = { fontSize: 11, color: 'var(--ds-muted)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 };
